import { GraphQLClientError } from '@/services/errors/GraphQLClientError'
import type {
  PriceIntentConfirmMutation,
  PriceIntentConfirmMutationVariables,
  PriceIntentCreateMutation,
  PriceIntentCreateMutationVariables,
  PriceIntentDataUpdateMutation,
  PriceIntentDataUpdateMutationVariables,
  PriceIntentQuery,
  PriceIntentQueryVariables,
  PriceIntentUpdateAndConfirmMutation,
  PriceIntentUpdateAndConfirmMutationVariables,
} from '@/services/graphql/generated'
import {
  PriceIntentConfirmDocument,
  PriceIntentCreateDocument,
  PriceIntentDataUpdateDocument,
  PriceIntentDocument,
  PriceIntentUpdateAndConfirmDocument,
} from '@/services/graphql/generated'
import type { Logger } from '@/services/logger/logger.types'
import type { SimplePersister } from '@/services/persister/Persister.types'
import type { PriceTemplate } from '@/services/PriceCalculator/PriceCalculator.types'
import { SHOP_SESSION_COOKIE_MAX_AGE } from '@/services/shopSession/ShopSession.constants'
import { type NextCookiesStore } from '@/utils/types'
import type { ApolloClient } from '@apollo/client'
import { AppRouterCookiePersister } from '../persister/AppRouterCookiePersister'
import { ClientCookiePersister } from '../persister/ClientCookiePersister'
import type { PriceIntent, PriceIntentAttribution, PriceIntentCreateParams } from './priceIntent.types'

type PriceIntentConfirmVariables = PriceIntentConfirmMutationVariables & {
  attribution?: PriceIntentAttribution
  generateRecommendations?: boolean
}

export class PriceIntentService {
  constructor(
    private readonly persister: SimplePersister,
    private readonly apolloClient: ApolloClient<unknown>,
    private readonly logger?: Logger,
  ) {}
  private createPromise: Promise<PriceIntent> | null = null

  public async create(params: PriceIntentCreateParams) {
    if (this.createPromise) return await this.createPromise

    // Deduplicate calls to create price intent
    this.createPromise = new Promise((resolve) => {
      this.createPriceIntent(params)
        .then((result) => resolve(result))
        .finally(() => (this.createPromise = null))
    })

    return await this.createPromise
  }

  public async get(priceIntentId: string) {
    try {
      const result = await this.apolloClient.query<PriceIntentQuery, PriceIntentQueryVariables>({
        query: PriceIntentDocument,
        variables: { priceIntentId },
      })
      return result.data.priceIntent
    } catch (error) {
      const message = `Unable to get price intent: ${priceIntentId}`
      if (this.logger) {
        this.logger.warn(message, { error })
      } else {
        console.warn(message, error)
      }
    }

    return null
  }

  public async getOrCreate(params: FetchParams) {
    const priceIntentId = this.getStoredId(params.priceTemplate.name, params.shopSessionId)

    if (priceIntentId) {
      const priceIntent = await this.get(priceIntentId)
      if (priceIntent != null && priceIntent.product.name === params.productName) {
        return priceIntent
      }
    }

    return await this.create({
      shopSessionId: params.shopSessionId,
      productName: params.productName,
      priceTemplate: params.priceTemplate,
    })
  }

  public async update(variables: PriceIntentDataUpdateMutationVariables) {
    const updatedResult = await this.apolloClient.mutate<
      PriceIntentDataUpdateMutation,
      PriceIntentDataUpdateMutationVariables
    >({
      mutation: PriceIntentDataUpdateDocument,
      variables,
    })
    const { priceIntent } = updatedResult.data?.priceIntentDataUpdate ?? {}
    if (!priceIntent) {
      throw new GraphQLClientError('Could not update price intent with initial data', 'PriceIntentDataUpdate', {
        priceIntentId: variables.priceIntentId,
      })
    }
    return priceIntent
  }

  public async confirm(priceIntentId: string, attribution?: PriceIntentAttribution, generateRecommendations?: boolean) {
    const updatedResult = await this.apolloClient.mutate<PriceIntentConfirmMutation, PriceIntentConfirmVariables>({
      mutation: PriceIntentConfirmDocument,
      variables: {
        priceIntentId,
        ...(attribution != null ? { attribution } : {}),
        ...(generateRecommendations != null ? { generateRecommendations } : {}),
      },
    })
    const { priceIntent } = updatedResult.data?.priceIntentConfirm ?? {}
    if (!priceIntent)
      throw new GraphQLClientError('Could not confirm price intent', 'PriceIntentConfirm', { priceIntentId })
    return priceIntent
  }

  public async updateAndConfirm(variables: PriceIntentUpdateAndConfirmMutationVariables) {
    const updatedResult = await this.apolloClient.mutate<
      PriceIntentUpdateAndConfirmMutation,
      PriceIntentUpdateAndConfirmMutationVariables
    >({
      mutation: PriceIntentUpdateAndConfirmDocument,
      variables,
    })

    const { priceIntent } = updatedResult.data?.priceIntentConfirm ?? {}
    if (!priceIntent)
      throw new GraphQLClientError('Could not update and confirm price intent', 'PriceIntentUpdateAndConfirm', {
        priceIntentId: variables.priceIntentId,
      })

    return priceIntent
  }

  public getStoredId(templateName: string, shopSessionId: string) {
    return this.persister.fetch(this.getPriceIntentKey(templateName, shopSessionId))
  }

  public save(params: { templateName: string; priceIntentId: string; shopSessionId: string }) {
    this.persister.save(params.priceIntentId, this.getPriceIntentKey(params.templateName, params.shopSessionId), {
      sameSite: 'none',
      secure: true,
    })
  }

  public clear(templateName: string, shopSessionId: string) {
    this.persister.reset(this.getPriceIntentKey(templateName, shopSessionId))
  }

  private getPriceIntentKey(templateName: string, shopSessionId: string) {
    return `HEDVIG_${shopSessionId}_${templateName}`
  }

  private async createPriceIntent({ productName, priceTemplate, shopSessionId }: PriceIntentCreateParams) {
    const result = await this.apolloClient.mutate<PriceIntentCreateMutation, PriceIntentCreateMutationVariables>({
      mutation: PriceIntentCreateDocument,
      variables: {
        productName,
        shopSessionId,
      },
      update: (cache, result) => {
        const priceIntent = result.data?.priceIntentCreate
        if (priceIntent) {
          cache.writeQuery({
            query: PriceIntentDocument,
            variables: { priceIntentId: priceIntent.id },
            data: { priceIntent },
          })
        }
      },
    })

    const priceIntent = result.data?.priceIntentCreate
    if (!priceIntent)
      throw new GraphQLClientError('Could not create price intent', 'PriceIntentCreate', { productName, shopSessionId })

    this.save({ priceIntentId: priceIntent.id, templateName: priceTemplate.name, shopSessionId })

    return priceIntent
  }
}

type FetchParams = {
  productName: string
  priceTemplate: PriceTemplate
  shopSessionId: string
}

let PRICE_INTENT_SERVICE: PriceIntentService | null = null

export const priceIntentServiceInitClientSide = (apolloClient: ApolloClient<unknown>, logger?: Logger) => {
  PRICE_INTENT_SERVICE ??= new PriceIntentService(
    new ClientCookiePersister('UNUSED_DEFAULT_KEY', SHOP_SESSION_COOKIE_MAX_AGE),
    apolloClient,
    logger,
  )
  return PRICE_INTENT_SERVICE
}

type ServerSideParams = {
  apolloClient: ApolloClient<unknown>
  cookieStore: NextCookiesStore
  logger?: Logger
}

export const priceIntentServiceInitServerSide = ({ cookieStore, apolloClient, logger }: ServerSideParams) => {
  return new PriceIntentService(
    new AppRouterCookiePersister(cookieStore, 'UNUSED_DEFAULT_KEY', SHOP_SESSION_COOKIE_MAX_AGE),
    apolloClient,
    logger,
  )
}
